// src/pages/BulkUploadPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FileSpreadsheet, Download, X, CheckCircle, AlertCircle,
  Loader2, Plus, Trash2, Globe, MapPin, FileDown, Edit2, Save,
  Eye, Search, Filter, RefreshCw
} from 'lucide-react';
import { 
  getClientDestinations, 
  bulkUploadActivities, 
  addSingleActivity, 
  downloadTemplate,
  getActivitiesByDestination,
  updateActivity,
  deleteActivity
} from '../api/bulkUpload.service';

const BulkUploadPage = () => {
  const [activeTab, setActiveTab] = useState('bulk');
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  
  // Manage activities state
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [editActivityId, setEditActivityId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDestination, setFilterDestination] = useState('');
  
  const [editFormData, setEditFormData] = useState({
    id: null,
    description: '',
    sic_price: '',
    pvt_2pax_price: '',
    pvt_4pax_price: '',
    pvt_6pax_price: '',
    pvt_8pax_price: '',
    pvt_10pax_price: '',
    sic_child_price: '',
    child_price: ''
  });
  
  const fileInputRef = useRef(null);
  const editInputRef = useRef(null);
  
  const [singleActivity, setSingleActivity] = useState({
    destination_id: '',
    description: '',
    sic_price: '',
    pvt_2pax_price: '',
    pvt_4pax_price: '',
    pvt_6pax_price: '',
    pvt_8pax_price: '',
    pvt_10pax_price: '',
    sic_child_price: '',
    child_price: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editActivityId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editActivityId]);

  useEffect(() => {
    fetchDestinations();
  }, []);

  useEffect(() => {
    if (activeTab === 'manage' && filterDestination) {
      loadActivities();
    }
  }, [filterDestination, activeTab]);

  const fetchDestinations = async () => {
    try {
      const response = await getClientDestinations();
      if (response.status) {
        setDestinations(response.data);
      }
    } catch (err) {
      console.error('Error fetching destinations:', err);
    }
  };

  const loadActivities = async () => {
    if (!filterDestination) return;
    
    setLoadingActivities(true);
    try {
      const response = await getActivitiesByDestination(filterDestination);
      if (response.status) {
        setActivities(response.data);
      }
    } catch (err) {
      console.error('Error loading activities:', err);
      alert('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleEditActivity = (activity) => {
    setEditActivityId(activity.id);
    setEditFormData({
      id: activity.id,
      description: activity.description || '',
      sic_price: activity.sic_price || '',
      pvt_2pax_price: activity.pvt_2pax_price || '',
      pvt_4pax_price: activity.pvt_4pax_price || '',
      pvt_6pax_price: activity.pvt_6pax_price || '',
      pvt_8pax_price: activity.pvt_8pax_price || '',
      pvt_10pax_price: activity.pvt_10pax_price || '',
      sic_child_price: activity.sic_child_price || '',
      child_price: activity.child_price || ''
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateActivity = async () => {
    if (!editFormData.description) {
      alert('Please enter description');
      return;
    }

    setSubmitting(true);
    try {
      const response = await updateActivity(editActivityId, editFormData);
      if (response.status) {
        alert('Activity updated successfully!');
        setEditActivityId(null);
        loadActivities();
      } else {
        alert(response.message);
      }
    } catch (err) {
      console.error('Error updating activity:', err);
      alert('Failed to update activity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditActivityId(null);
  };

  const resetForm = () => {
    setSingleActivity({
      destination_id: '',
      description: '',
      sic_price: '',
      pvt_2pax_price: '',
      pvt_4pax_price: '',
      pvt_6pax_price: '',
      pvt_8pax_price: '',
      pvt_10pax_price: '',
      sic_child_price: '',
      child_price: ''
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        alert('Please upload Excel file (.xlsx, .xls, .csv)');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };
const handleBulkUpload = async () => {
  console.log('🚀 Starting bulk upload...');
  console.log('Selected destination:', selectedDestination);
  console.log('File:', file);
  
  if (!selectedDestination) {
    alert('Please select a destination');
    return;
  }
  if (!file) {
    alert('Please select a file');
    return;
  }

  setUploading(true);
  try {
    // 🔥 CRITICAL: Create FormData correctly
    const formData = new FormData();
    formData.append('file', file);  // Make sure field name is 'file'
    formData.append('destination_id', selectedDestination);
    
    // Debug: Check FormData contents
    console.log('📦 FormData created:');
    for (let pair of formData.entries()) {
      console.log('   ', pair[0], pair[1]);
    }
    
    const response = await bulkUploadActivities(formData);
    console.log('✅ Upload response:', response);
    
    setUploadResult(response);
    if (response.status) {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  } catch (err) {
    console.error('❌ Upload error:', err);
    setUploadResult({
      status: false,
      message: err.response?.data?.message || err.message || 'Upload failed'
    });
  } finally {
    setUploading(false);
  }
};

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      await downloadTemplate();
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleActivity.destination_id) {
      alert('Please select a destination');
      return;
    }
    if (!singleActivity.description) {
      alert('Please enter description');
      return;
    }

    setSubmitting(true);
    try {
      const response = await addSingleActivity(singleActivity);
      if (response.status) {
        alert('Activity added successfully!');
        resetForm();
        if (activeTab === 'manage') {
          loadActivities();
        }
      } else {
        alert(response.message);
      }
    } catch (err) {
      console.error('Error adding activity:', err);
      alert('Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSingleActivity(prev => ({ ...prev, [name]: value }));
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploadResult(null);
  };

  const filteredActivities = activities.filter(act => 
    act.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ActivityCard = ({ activity }) => {
    const isEditing = editActivityId === activity.id;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              ref={editInputRef}
              name="description"
              value={editFormData.description}
              onChange={handleEditInputChange}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm"
              placeholder="Description"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">SIC Price</label>
                <input
                  type="number"
                  name="sic_price"
                  value={editFormData.sic_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">SIC Child Price</label>
                <input
                  type="number"
                  name="sic_child_price"
                  value={editFormData.sic_child_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">PVT 2 Pax</label>
                <input
                  type="number"
                  name="pvt_2pax_price"
                  value={editFormData.pvt_2pax_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">PVT 4 Pax</label>
                <input
                  type="number"
                  name="pvt_4pax_price"
                  value={editFormData.pvt_4pax_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">PVT 6 Pax</label>
                <input
                  type="number"
                  name="pvt_6pax_price"
                  value={editFormData.pvt_6pax_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">PVT 8 Pax</label>
                <input
                  type="number"
                  name="pvt_8pax_price"
                  value={editFormData.pvt_8pax_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">PVT 10 Pax</label>
                <input
                  type="number"
                  name="pvt_10pax_price"
                  value={editFormData.pvt_10pax_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Child Price (PVT)</label>
                <input
                  type="number"
                  name="child_price"
                  value={editFormData.child_price}
                  onChange={handleEditInputChange}
                  step="0.01"
                  className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleUpdateActivity}
                disabled={submitting}
                className="flex-1 bg-green-500 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-green-600 flex items-center justify-center gap-1"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm font-medium text-gray-800 flex-1">{activity.description}</p>
              <div className="flex gap-1 ml-2">
                <button
                  onClick={() => handleEditActivity(activity)}
                  className="p-1 hover:bg-amber-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 size={16} className="text-amber-500" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {activity.sic_price > 0 && (
                <div className="bg-gray-50 p-1 rounded">
                  <span className="text-gray-500">SIC:</span>
                  <span className="ml-1 font-medium">{activity.sic_price} THB</span>
                </div>
              )}
              {activity.sic_child_price > 0 && (
                <div className="bg-gray-50 p-1 rounded">
                  <span className="text-gray-500">SIC Child:</span>
                  <span className="ml-1 font-medium">{activity.sic_child_price} THB</span>
                </div>
              )}
              {activity.pvt_2pax_price > 0 && (
                <div className="bg-gray-50 p-1 rounded">
                  <span className="text-gray-500">PVT 2P:</span>
                  <span className="ml-1 font-medium">{activity.pvt_2pax_price} THB</span>
                </div>
              )}
              {activity.pvt_4pax_price > 0 && (
                <div className="bg-gray-50 p-1 rounded">
                  <span className="text-gray-500">PVT 4P:</span>
                  <span className="ml-1 font-medium">{activity.pvt_4pax_price} THB</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Activity Management</h1>
          <p className="text-gray-500 text-sm mt-1">Upload, add, edit and manage your activities</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('bulk'); resetForm(); setEditActivityId(null); }}
            className={`px-5 py-2.5 font-medium transition-all border-b-2 ${
              activeTab === 'bulk'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload size={18} className="inline mr-2" />
            Bulk Upload
          </button>
          <button
            onClick={() => { setActiveTab('single'); resetForm(); setEditActivityId(null); }}
            className={`px-5 py-2.5 font-medium transition-all border-b-2 ${
              activeTab === 'single'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Plus size={18} className="inline mr-2" />
            Add Single
          </button>
          <button
            onClick={() => { setActiveTab('manage'); resetForm(); setEditActivityId(null); }}
            className={`px-5 py-2.5 font-medium transition-all border-b-2 ${
              activeTab === 'manage'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye size={18} className="inline mr-2" />
            Manage Activities
          </button>
          <button
            onClick={handleDownloadTemplate}
            disabled={downloading}
            className="px-5 py-2.5 font-medium text-gray-500 hover:text-gray-700 transition-all"
          >
            {downloading ? <Loader2 size={18} className="animate-spin inline mr-2" /> : <FileDown size={18} className="inline mr-2" />}
            Download Template
          </button>
        </div>

        {/* Bulk Upload Tab */}
        {activeTab === 'bulk' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Bulk Upload from Excel</h2>
            
            {/* Destination Selection */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Destination <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedDestination}
                  onChange={(e) => setSelectedDestination(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                >
                  <option value="">Select a destination</option>
                  {destinations.map(dest => (
                    <option key={dest.id} value={dest.id}>
                      {dest.destination_name} {dest.country_name ? `(${dest.country_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Excel File <span className="text-red-500">*</span>
              </label>
              {!file ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors">
                  <input
                    ref={fileInputRef}
                    id="fileInput"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="fileInput" className="cursor-pointer block">
                    <FileSpreadsheet size={48} className="mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-400 mt-1">Excel files only (.xlsx, .xls, .csv)</p>
                  </label>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between border border-gray-200">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet size={24} className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <button onClick={clearFile} className="p-1 hover:bg-gray-200 rounded-full">
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>
              )}
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`p-4 rounded-lg mb-5 ${
                uploadResult.status ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {uploadResult.status ? (
                    <CheckCircle size={20} className="text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${uploadResult.status ? 'text-green-800' : 'text-red-800'}`}>
                      {uploadResult.message}
                    </p>
                    {uploadResult.data && (
                      <div className="mt-2 text-sm">
                        <p>Total: {uploadResult.data.total}</p>
                        <p>Inserted: {uploadResult.data.inserted}</p>
                        <p>Failed: {uploadResult.data.failed}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleBulkUpload}
              disabled={uploading || !selectedDestination || !file}
              className="w-full bg-amber-500 text-white py-2.5 rounded-lg font-medium hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
              {uploading ? 'Uploading...' : 'Upload Activities'}
            </button>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-2">Excel Format Instructions:</h3>
              <div className="text-sm text-gray-500 space-y-1">
                <p>• First row should be headers (will be skipped)</p>
                <p>• Supported column names (case insensitive):</p>
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li><strong>SIC Price</strong> - or "SIC", "SIC Adult"</li>
                  <li><strong>SIC Child Price</strong> - or "SIC Child"</li>
                  <li><strong>PVT 2 Pax Price</strong> - or "PVT 2", "2 Pax"</li>
                  <li><strong>PVT 4 Pax Price</strong> - or "PVT 4", "4 Pax"</li>
                  <li><strong>PVT 6 Pax Price</strong> - or "PVT 6", "6 Pax"</li>
                  <li><strong>PVT 8 Pax Price</strong> - or "PVT 8", "8 Pax"</li>
                  <li><strong>PVT 10 Pax Price</strong> - or "PVT 10", "10 Pax"</li>
                  <li><strong>PVT Child Price</strong> - or "Child Price PVT"</li>
                  <li><strong>DESCRIPTION</strong> - or "Activity", "Activity Description" (Required)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Single Activity Form Tab */}
        {activeTab === 'single' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Add Single Activity</h2>
            
            <form onSubmit={handleSingleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destination <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    name="destination_id"
                    value={singleActivity.destination_id}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    required
                  >
                    <option value="">Select a destination</option>
                    {destinations.map(dest => (
                      <option key={dest.id} value={dest.id}>
                        {dest.destination_name} {dest.country_name ? `(${dest.country_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={singleActivity.description}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  placeholder="Enter activity description"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SIC Price</label>
                  <input
                    type="number"
                    name="sic_price"
                    value={singleActivity.sic_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SIC Child Price</label>
                  <input
                    type="number"
                    name="sic_child_price"
                    value={singleActivity.sic_child_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT 2 Pax Price</label>
                  <input
                    type="number"
                    name="pvt_2pax_price"
                    value={singleActivity.pvt_2pax_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT 4 Pax Price</label>
                  <input
                    type="number"
                    name="pvt_4pax_price"
                    value={singleActivity.pvt_4pax_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT 6 Pax Price</label>
                  <input
                    type="number"
                    name="pvt_6pax_price"
                    value={singleActivity.pvt_6pax_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT 8 Pax Price</label>
                  <input
                    type="number"
                    name="pvt_8pax_price"
                    value={singleActivity.pvt_8pax_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT 10 Pax Price</label>
                  <input
                    type="number"
                    name="pvt_10pax_price"
                    value={singleActivity.pvt_10pax_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PVT Child Price</label>
                  <input
                    type="number"
                    name="child_price"
                    value={singleActivity.child_price}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-500 text-white py-2.5 rounded-lg font-medium hover:bg-green-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
                {submitting ? 'Adding...' : 'Add Activity'}
              </button>
            </form>
          </div>
        )}

        {/* Manage Activities Tab */}
        {activeTab === 'manage' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Manage Activities</h2>
            
            <div className="flex flex-wrap gap-3 mb-5">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Destination</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={filterDestination}
                    onChange={(e) => setFilterDestination(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  >
                    <option value="">Select Destination</option>
                    {destinations.map(dest => (
                      <option key={dest.id} value={dest.id}>
                        {dest.destination_name} {dest.country_name ? `(${dest.country_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                </div>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={loadActivities}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-2"
                >
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>
            </div>

            {loadingActivities ? (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-amber-500" size={32} />
              </div>
            ) : !filterDestination ? (
              <div className="text-center py-12">
                <Filter size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Select a destination to view activities</p>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="text-center py-12">
                <Eye size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No activities found for this destination</p>
                <button
                  onClick={() => setActiveTab('single')}
                  className="mt-3 text-amber-500 hover:text-amber-600 text-sm font-medium"
                >
                  + Add a new activity
                </button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredActivities.map(activity => (
                  <ActivityCard key={activity.id} activity={activity} />
                ))}
              </div>
            )}
            
            {filteredActivities.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                Total: {filteredActivities.length} activities
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadPage;